import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { RecipeDetailResponse } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  getBannerReferer,
  getItemCardTrackingParams,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useRouteQuery, useRouteRedirection } from '@diamond/sol-buyer/utils';
import {
  useIsAuthenticated,
  useShipToStore,
  useUpdateBatchCart,
} from '@diamond/sol-buyer-context';
import { isAxiosError } from 'axios';

import { RecipeCartProductSelect } from './RecipeCartProductSelect';
import { useRecipeAddToCart } from './useRecipeAddToCart';

type RecipeCartModalProps = {
  data: RecipeDetailResponse;
  countItems: number;
  isDisabled?: boolean;
  isRecommended: boolean;
};

export function RecipeCartModal({
  data,
  countItems,
  isDisabled,
  isRecommended,
}: RecipeCartModalProps) {
  const slug = useRouteQuery()['recipesId'];
  const isAuthenticated = useIsAuthenticated();
  const { withAuth } = useRouteRedirection({ isAuthenticated });
  const warehouse = useShipToStore((s) => s.selectedShipToId);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    products,
    totalUnavailableProduct,
    selectedAvailableProducts,
    totalHPP,
    totalProduct,
    handleChangeProduct,
    handleDeleteProduct,
    resetState,
  } = useRecipeAddToCart(data, isRecommended);

  const batchUpdateCartMutation = useUpdateBatchCart({
    onSuccess() {
      showToast(
        toast,
        'success',
        `${totalProduct} Produk berhasil masuk keranjang`
      );

      const bannerReferer = getBannerReferer();

      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: totalHPP,
        items: selectedAvailableProducts.map((product, index) => {
          const trackingParams = getItemCardTrackingParams(
            product as unknown as Parameters<
              typeof getItemCardTrackingParams
            >[0], // Cast to avoid type mismatch
            1,
            TRACKING_PAGES.RECIPE_DETAIL,
            TRACKING_PAGES.RECIPE_DETAIL
          );

          return {
            ...trackingParams,
            index: index + 1,
            affiliation: 'Sukanda Onelink',
            location_id: warehouse,
          };
        }),
        section_name: TRACKING_PAGES.RECIPE_DETAIL,
        id_recipe: data?.id, // Keep recipe-specific fields for backward compatibility
        recipe_name: data?.title,
        recipe_slug: slug,
        total_products: totalProduct,
        warehouse_id: warehouse,
        ...(bannerReferer && {
          referer_source: bannerReferer.referer_source,
          banner_id: bannerReferer.banner_id,
          banner_name: bannerReferer.banner_name,
        }),
      });
      onClose();
    },
    onError(err) {
      let message = 'Unknown Error';
      if (isAxiosError(err)) {
        message = err.response?.data?.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      showToast(toast, 'error', message);
    },
  });

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      <Button
        onClick={() => {
          withAuth(onOpen, 'recipes', slug);
        }}
        color="white"
        _hover={{ bg: 'blue.700' }}
        w="full"
        size={{ md: 'sm', xl: 'md' }}
        isDisabled={isDisabled}
      >
        + Keranjang ({countItems})
      </Button>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent maxW="xl">
          <ModalHeader textAlign="center">
            Konfirmasi Produk dari Resep
            <Text
              textAlign="center"
              fontWeight="normal"
              fontSize={{ md: 'xs', xl: 'sm' }}
              maxW="sm"
              mx="auto"
              mt="2"
            >
              Tekan Panah untuk ubah produk dari resep. <br /> Ganti kuantitas
              dapat dilakukan pada halaman keranjang.
            </Text>
          </ModalHeader>

          <ModalCloseButton />
          <ModalBody>
            <VStack alignItems="flex-start" spacing="4">
              {totalUnavailableProduct > 0 ? (
                <Flex
                  w="full"
                  px="8"
                  py="2"
                  bg="gray.customGray"
                  color="red"
                  alignItems="center"
                  rounded="lg"
                  fontSize="sm"
                >
                  {totalUnavailableProduct} bahan pada resep saat ini tidak
                  tersedia
                </Flex>
              ) : null}

              {products.map(
                ({ order: product, selected: selectedIdx }, index) => (
                  <RecipeCartProductSelect
                    key={product[0].id}
                    product={product}
                    selectedProductIdx={selectedIdx}
                    handleNavigate={(productId) =>
                      handleChangeProduct({
                        index,
                        productId,
                      })
                    }
                    handleDelete={() => handleDeleteProduct(index)}
                  />
                )
              )}
              <Flex
                w="full"
                px="8"
                py="2.5"
                bg="#D9F3FF"
                alignItems="center"
                rounded="lg"
              >
                <Text
                  flex="1 1 calc(70px + 50%)"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  Total harga • {totalProduct} Produk
                </Text>
                <Text
                  flex="1 1 calc(50% - 5px)"
                  fontWeight="bold"
                  fontSize="xl"
                >
                  {buyerCurrencyFormatter(totalHPP)}
                </Text>
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="outline" mr={3} onClick={handleClose}>
              Kembali
            </Button>
            <Button
              isDisabled={totalProduct === 0 || totalHPP === 0}
              colorScheme="blue"
              onClick={() =>
                batchUpdateCartMutation.mutate(
                  selectedAvailableProducts.map((p) => ({
                    product_id: p.id,
                    quantity: p.minimum_order_quantity,
                    warehouse_id: warehouse,
                    note: '',
                  }))
                )
              }
            >
              Konfirmasi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
